import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, CheckCircle2, CircleAlert, MinusCircle, PlusCircle, ScanLine, Trash2, Upload, X } from "lucide-react";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { convertDateFormatInUtc, convertDateToCT } from "../../../utils/util";
import { createInboundShipment, listAllItemsByTrackingId, uploadInboundShipmentImage } from "../../../reducers/warehouse.slice";
import { getInvoiceDetails, updateShipment, updateShipmentTracking } from "../../../reducers/shipment.slice";
import { generatePDF } from "../../../utils/generateInvoice";
import { uploadPayoutInvoice } from "../../../reducers/payouts.slice";
import { getUpcLookupByUpc } from "../../../reducers/quote.slice";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import DataTable from "../../../components/shared/DataTable";
import PageHeader from "../../../components/shared/PageHeader";
import SearchBar from "../../../components/shared/SearchBar";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const blockInvalidInt = (e) => {
  const invalidChars = ["e", "E", ".", ",", "+", "-"];
  if (
    invalidChars.includes(e.key) ||
    (!/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey)
  ) {
    e.preventDefault();
  }
};

const helperTextStyle = { color: "#f27059", fontSize: 11, marginTop: 5 };
const panelStyle = { background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 };

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <Label style={{ color: "#8b9296", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>{label}</Label>
      {children}
    </div>
  );
}

function ActionButton({ title, disabled, onClick, color, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onClick?.();
          }}
          style={{
            border: `1px solid ${disabled ? "rgba(255,255,255,0.14)" : color}`,
            background: disabled ? "rgba(255,255,255,0.035)" : `${color}1A`,
            color: disabled ? "#73787c" : color,
          }}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

const WarehouseTrackInboundItem = () => {
  const user = getUsers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const { userDetails } = useContext(AuthContext);
  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [serialNo, setSerialNo] = useState("");
  const [note, setNote] = useState("");
  const [openAddDetailsDialog, setOpenAddDetailsDialog] = useState(false);
  const [openDetailsUpdatedDialog, setOpenDetailsUpdatedDialog] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [inboundItemDetails, setInboundItemDetails] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [multiRowsSelected, setMultiRowsSelected] = useState(false);
  const [selectedRowsHistory, setSelectedRowsHistory] = useState([]);
  const [openAllApprovalDialog, setOpenAllApprovalDialog] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [resetSelection, setResetSelection] = useState(false);
  const [inboundItemStatusDetails, setInboundItemStatusDetails] = useState([]);
  const [addNewItems, setAddNewItems] = useState(false);
  const [shipmentId, setShipmentId] = useState("");
  const [openAddItemDialog, setOpenAddItemDialog] = useState(false);
  const [rejectShipmentDetails, setRejectShipmentDetails] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [newItem, setNewItem] = useState({ upc: "", name: "", shipmentId: "", trackingId: "", serialNo: "" });
  const [trackingIdError, setTrackingIdError] = useState("");
  const [disableButtons, setDisableButtons] = useState(() => {
    const storedTrackingId = sessionStorage.getItem("trackingId");
    return !(storedTrackingId && storedTrackingId.trim() !== "");
  });
  const [trackingItemNameError, setTrackingItemNameError] = useState("");
  const [disableAddItem, setDisableAddItem] = useState(true);
  const [isTrackingDataAvailable, setIsTrackingDataAvailable] = useState(false);
  const [searchResult, setSearchResult] = useState("");
  const [newTrackingId, setNewTrackingId] = useState("");
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [trackingInboundDetails, setTrackingInboundDetails] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const inputRefs = useRef({});
  const [openScanDialog, setOpenScanDialog] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [scanError, setScanError] = useState("");
  const [scanSerials, setScanSerials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(Number(sessionStorage.getItem("tablePage")) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(sessionStorage.getItem("page")) || 5);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleAddNewItem = () => setOpenAddItemDialog(true);
  const handleNewItemDialogClose = () => {
    setNewItem({ upc: "", name: "", shipmentId: "", trackingId: "", serialNo: "", manualEntry: true });
    setTrackingItemNameError("");
    setOpenAddItemDialog(false);
  };

  const handleOpenScanDialog = () => {
    setScanInput("");
    setScanError("");
    setOpenScanDialog(true);
  };

  const handleCloseScanDialog = () => {
    setOpenScanDialog(false);
    setScanInput("");
    setScanError("");
  };

  const handleScanInputChange = (e) => {
    setScanInput(e.target.value);
  };

  const handleScanPaste = (e) => {
    const pasteData = e.clipboardData.getData("text");
    const newSerials = pasteData.split(/[\r\n,;\t ]+/).map((v) => v.trim()).filter(Boolean);
    setScanSerials((prev) => [...prev, ...newSerials]);
    setScanInput("");
    setScanError("");
    e.preventDefault();
  };

  const handleScanKeyDown = (e) => {
    if (e.key === "Enter" && scanInput.trim()) {
      setScanSerials((prev) => [...prev, scanInput.trim()]);
      setScanInput("");
      setScanError("");
      e.preventDefault();
    }
  };

  const handleDeleteChip = (chipToDelete) => {
    setScanSerials((chips) => {
      const updated = chips.filter((chip) => chip !== chipToDelete);
      if (updated.length > 0) setScanError("");
      return updated;
    });
  };

  const handleScanSubmit = () => {
    if (scanSerials.length === 0) {
      setScanError("Please scan or paste at least one value.");
      return;
    }

    const scannedUPCCounts = scanSerials.reduce((acc, upc) => {
      acc[upc] = (acc[upc] || 0) + 1;
      return acc;
    }, {});

    const availableUPCCounts = inboundItemDetails.reduce((acc, item) => {
      acc[item.upc] = (acc[item.upc] || 0) + 1;
      return acc;
    }, {});

    const exceededUPCs = [];
    for (const [upc, scannedCount] of Object.entries(scannedUPCCounts)) {
      const availableCount = availableUPCCounts[upc] || 0;
      if (scannedCount > availableCount) exceededUPCs.push(`UPC ${upc}: scanned ${scannedCount}, available ${availableCount}`);
    }

    if (exceededUPCs.length > 0) {
      setScanError(`Scanned quantity exceeds available quantity: ${exceededUPCs.join(", ")}`);
      return;
    }

    const matchedRows = [];
    const availableItems = [...inboundItemDetails];
    for (const item of availableItems) {
      const upc = item.upc;
      if (scannedUPCCounts[upc] && scannedUPCCounts[upc] > 0) {
        matchedRows.push(item);
        scannedUPCCounts[upc]--;
      }
    }

    if (matchedRows.length === 0) {
      setScanError("No matching UPCs found in the table.");
      return;
    }

    setFilteredData(matchedRows);
    setCurrentPage(0);
    sessionStorage.setItem("tablePage", 0);
    setOpenScanDialog(false);
    setScanInput("");
    setScanSerials([]);
    setScanError("");
  };

  const handleAddItemSubmit = () => {
    const now = new Date();
    const itemsToAdd = [];
    for (let i = 0; i < quantity; i++) {
      itemsToAdd.push({
        id: now.getTime() + 1,
        name: newItem?.name,
        upc: newItem?.upc,
        serialNo: newItem?.serialNo,
        trackingId: trackingId,
        shipmentDate: convertDateFormatInUtc(new Date(), "MMMM D, YYYY hh:mm A"),
        ...(newItem.shipmentId && newItem.shipmentId.trim() !== "" ? { shipmentId: newItem.shipmentId } : {}),
        manualEntry: true,
      });
    }
    setDisableButtons(false);
    setFilteredData((prev) => {
      const updated = [...itemsToAdd, ...prev];
      sessionStorage.setItem("newItems", JSON.stringify(updated));
      return updated;
    });
    setInboundItemDetails((prev) => [...itemsToAdd, ...prev]);
    setOpenAddItemDialog(false);
    setNewItem({ upc: "", name: "", shipmentId: "", trackingId: "", serialNo: "" });
    setQuantity(1);
  };

  const handleUpdateStatus = () => {
    setShowLoader(true);
    const payload = { id: trackingInboundDetails?.id, status: "SHIPPED", shipment_type: trackingInboundDetails?.shipment_type };
    dispatch(updateShipment(payload)).then(async (res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (!res?.payload?.generate_invoice) {
        setShowLoader(false);
        navigate(-1);
        return;
      }
      await dispatch(getInvoiceDetails({ id: trackingInboundDetails?.id })).then(async (response) => {
        if (response?.error && Object.keys(response?.error)?.length > 0) {
          setShowLoader(false);
          showToast(response?.error?.message, "error");
          return;
        }

        const formdata = generatePDF(response?.payload);
        const uploadPayload = {
          id: res?.payload?.payout_id,
          document_type: "INVOICE",
          invoice_number: response?.payload?.invoice_number,
        };

        await dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
          if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
            setShowLoader(false);
            showToast(uploadResponse?.error?.message, "error");
            return;
          }
        });
      });

      showToast("Status updated successfully", "success");
      setOpenStatusDialog(false);
      handleSearchByTrackingID();
      setShowLoader(false);
    });
  };

  const handleSelectionChange = (selectedRows) => {
    const enabledRows = selectedRows.filter((row) => !row.disabled);
    setSelectedRows(enabledRows);
    setMultiRowsSelected(enabledRows.length > 0);
  };

  const handleAllApprovalDialogOpen = () => setOpenAllApprovalDialog(true);
  const handleAllApprovalDialogClose = () => setOpenAllApprovalDialog(false);

  const disableNewItem = (row) => {
    setFilteredData((prev) => {
      const updated = prev.map((item) => (item.id === row.id ? { ...item, disabled: true } : item));
      updated.sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));
      sessionStorage.setItem("newItems", JSON.stringify(updated));
      return updated;
    });
    setInboundItemDetails((prev) => {
      const updated = prev.map((item) => (item.id === row.id ? { ...item, disabled: true } : item));
      updated.sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));
      return updated;
    });
  };

  const handleMultiItemsApproval = () => {
    setShowLoader(true);
    const request_list = [];
    selectedRows.forEach((item) => {
      request_list.push({
        upc: item?.upc,
        quantity: 1,
        item_name: item?.name,
        shipment_id: item?.shipmentId,
        inbound_tracking_number: trackingId,
        status: "GOOD",
        change_type: "SCAN",
        adjustment_type: "ADD",
        serial_no: inputValues[item?.id] || "",
        notes: "",
      });
    });
    dispatch(createInboundShipment({ request_list })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      selectedRows.forEach((item) => {
        disableNewItem(item);
      });
      setSelectedRowsHistory((prev) => [...prev, ...request_list]);
      setSelectedRows([]);
      setMultiRowsSelected(false);
      setResetSelection(true);
      setTimeout(() => setResetSelection(false), 100);
      showToast("Success", "success");
      setShowLoader(false);
    });
    setOpenAllApprovalDialog(false);
  };

  const handleAddDetailsDialogClose = () => {
    setSerialNo("");
    setNote("");
    setUploadedImages([]);
    setOpenAddDetailsDialog(false);
  };

  const handleItemDetails = () => {
    const merged = {};
    selectedRowsHistory.forEach((item) => {
      const key = `${item.upc}_${item.status}`;
      if (!merged[key]) {
        merged[key] = { upc: item.upc, qty: 0, status: item.status === "GOOD" ? "Ok" : "Damaged" };
      }
      merged[key].qty += item.quantity || 1;
    });
    setInboundItemStatusDetails(Object.values(merged));
    setOpenDetailsUpdatedDialog(true);
  };

  const handleDetailsUpdatedDialogClose = () => setOpenDetailsUpdatedDialog(false);

  const clearNewItems = () => {
    sessionStorage.removeItem("newItems");
  };

  const handleInboundDetailsUpdate = () => {
    setShowLoader(true);
    if (isTrackingDataAvailable) {
      const payload = { id: newTrackingId, status: "RECEIVED", shipment_id: shipmentId };
      if (!shipmentId || shipmentId === "") delete payload.shipment_id;

      dispatch(updateShipmentTracking(payload)).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setShowLoader(false);
          showToast(res?.error?.message, "error");
          return;
        }
        sessionStorage.removeItem("trackingId");
        clearNewItems();
        navigate("/warehouse/inventory");
      });
    } else {
      sessionStorage.removeItem("trackingId");
      clearNewItems();
      navigate("/warehouse/inventory");
    }
  };

  const handleSearchByTrackingID = () => {
    setShowLoader(true);
    setDisableAddItem(false);
    sessionStorage.setItem("trackingId", trackingId);
    dispatch(listAllItemsByTrackingId(trackingId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setIsTrackingDataAvailable(false);
        setSearchResult(trackingId);
        setFilteredData([]);
        setInboundItemDetails([]);
        setDisableButtons(true);
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.status === "LABEL_PENDING" || res?.payload?.status === "PENDING_SHIPMENT") {
        setTrackingInboundDetails(res?.payload);
        setOpenStatusDialog(true);
        setShowLoader(false);
      } else if (res?.payload?.status === "RECEIVED") {
        setDisableButtons(true);
        setAddNewItems(false);
        setSearchResult(trackingId);
        setShipmentId(res?.payload?.id);
        setNewTrackingId(res?.payload?.tracking_details?.id);
        setFilteredData([]);
        setInboundItemDetails([]);
        setDisableButtons(true);
        setShowLoader(false);
      } else {
        setIsTrackingDataAvailable(true);
        setAddNewItems(false);
        setSearchResult(trackingId);
        setShipmentId(res?.payload?.id);
        const response = res?.payload;
        console.log(response?.tracking_details?.id, "response?.tracking_details?.id");
        setNewTrackingId(response?.tracking_details?.id);
        const rows = [];
        response.shipment_items.forEach((item) => {
          let qty = item?.quantity - item?.received_quantity;
          for (let i = 0; i < qty; i++) {
            rows.push({
              id: `${response?.id}-${item.upc}-${i + 1}`,
              trackingNumber: newTrackingId,
              shipmentId: response?.id,
              shipmentDate: convertDateToCT(response?.shipment_date, "MMMM D, YYYY hh:mm A"),
              upc: item.upc,
              name: item.name,
              serial_no: "",
            });
          }
        });
        setFilteredData(rows);
        setInboundItemDetails(rows);
        setShowLoader(false);
        setDisableButtons(false);
      }
    });
  };

  const handleRowClick = (row) => {};
  const handleInputChange = (id, value) => {
    setInputValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleTrackingIdChange = (e) => {
    const value = e.target.value;
    setSearchResult("");
    setFilteredData([]);
    const regex = /^[a-zA-Z0-9-_]*$/;
    if (!value) {
      setTrackingIdError("");
      setDisableAddItem(true);
      setDisableButtons(true);
    } else if (!regex.test(value)) {
      setTrackingIdError("Special characters are not allowed.");
      setDisableButtons(true);
    } else {
      setTrackingIdError("");
    }
    clearNewItems();
    setTrackingId(value);
  };

  const handleDeleteManualShipment = (row) => {
    setShowLoader(true);
    setFilteredData((prev) => {
      const updated = prev.filter((item) => item.id !== row.id);
      sessionStorage.setItem("newItems", JSON.stringify(updated));
      if (updated.length === 0) setDisableButtons(true);
      return updated;
    });
    setInboundItemDetails((prev) => prev.filter((item) => item.id !== row.id));
    setShowLoader(false);
  };

  const handleApproveShipment = (row) => {
    setShowLoader(true);
    const request_list = [
      {
        upc: row?.upc,
        quantity: 1,
        item_name: row?.name,
        shipment_id: row?.shipmentId,
        inbound_tracking_number: trackingId,
        status: "GOOD",
        change_type: "SCAN",
        adjustment_type: "ADD",
        serial_no: inputValues[row.id] || "",
        notes: "",
      },
    ];
    dispatch(createInboundShipment({ request_list })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      disableNewItem(row);
      setSelectedRowsHistory((prev) => [...prev, ...request_list]);
      setSelectedRows([]);
      setResetSelection(true);
      setTimeout(() => setResetSelection(false), 100);
      showToast("Success", "success");
      setShowLoader(false);
    });
  };

  const handleImageUpload = (id) => {
    const formData = new FormData();
    uploadedImages.forEach((file) => formData.append("files", file));
    dispatch(uploadInboundShipmentImage({ formData, id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSelectedRows([]);
      setRejectShipmentDetails([]);
      showToast("Success", "success");
      setShowLoader(false);
    });
  };

  const handleRejectShipment = (row) => {
    setOpenAddDetailsDialog(true);
    setSerialNo(inputValues[row.id] || "");
    setRejectShipmentDetails(row);
  };

  const handleItemRejectDetails = () => {
    setShowLoader(true);
    const request_list = [
      {
        upc: rejectShipmentDetails?.upc,
        quantity: 1,
        item_name: rejectShipmentDetails?.name,
        shipment_id: rejectShipmentDetails?.shipmentId,
        inbound_tracking_number: trackingId,
        status: "DAMAGED",
        change_type: "SCAN",
        adjustment_type: "ADD",
        serial_no: serialNo,
        notes: note,
      },
    ];
    dispatch(createInboundShipment({ request_list })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      disableNewItem(rejectShipmentDetails);
      setSelectedRowsHistory((prev) => [...prev, ...request_list]);
      setResetSelection(true);
      if (uploadedImages.length > 0) {
        handleImageUpload(res?.payload[0]?.id);
      } else {
        setSelectedRows([]);
        setRejectShipmentDetails([]);
        showToast("Success", "success");
        setShowLoader(false);
      }
    });
    handleAddDetailsDialogClose();
  };

  const handleGetUpcData = (e) => {
    setShowLoader(true);
    const payload = { upc: e.target.value };
    dispatch(getUpcLookupByUpc(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      setNewItem((prev) => ({ ...prev, item_name: res?.payload?.title || "" }));
    });
  };

  const handleIncrease = () => {
    const currentQuantity = quantity === "" ? 0 : Number(quantity);
    handleQuantityChange(currentQuantity + 1);
  };

  const handleDecrease = () => {
    const currentQuantity = quantity === "" ? 0 : Number(quantity);
    if (currentQuantity > 1) handleQuantityChange(currentQuantity - 1);
  };

  const handleQuantityChange = (newValue) => {
    newValue = Number(newValue);
    if (newValue === 0) {
      setQuantity(newValue);
      return;
    }
    if (newValue < 0) {
      setQuantity(newValue);
      return;
    }
    setQuantity(newValue);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    const value = term.trim().toLowerCase();
    if (!value) {
      setSearchResults(inboundItemDetails);
      return;
    }
    setSearchResults(
      inboundItemDetails.filter(
        (item) =>
          (item?.name && String(item.name).toLowerCase().includes(value)) ||
          (item?.upc && String(item.upc).toLowerCase().includes(value))
      )
    );
  };

  const filterData = () => {
    let filtered = [...searchResults];
    setFilteredData(filtered);
    setCurrentPage(0);
    sessionStorage.setItem("tablePage", 0);
  };

  useEffect(() => {
    setSearchResults([]);
  }, []);

  useEffect(() => {
    filterData();
  }, [inboundItemDetails, searchResults]);

  useEffect(() => {
    setSearchResults(inboundItemDetails);
  }, [inboundItemDetails]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("trackingId");
      sessionStorage.removeItem("newItems");
    };
  }, [location]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem("tablePage", page);
  };

  const handleRowsPerPageChange = (pageSize) => {
    setRowsPerPage(pageSize);
    sessionStorage.setItem("page", pageSize);
    setCurrentPage(0);
    sessionStorage.setItem("tablePage", 0);
  };

  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const columns = [
    { key: "name", label: "Product Name", maxWidth: 260 },
    { key: "upc", label: "UPC", width: 130 },
    { key: "shipmentId", label: "Shipment ID", width: 160 },
    {
      key: "serialNo",
      label: "Serial No.",
      width: 180,
      render: (row) => {
        const isDisabled = row.disabled;
        return (
          <Input
            value={inputValues[row?.id] || ""}
            onChange={(e) => handleInputChange(row?.id, e.target.value)}
            placeholder="serial no."
            disabled={isDisabled}
            ref={(el) => {
              if (el) inputRefs.current[row.id] = el;
            }}
            onPaste={() => {
              setTimeout(() => {
                const ids = filteredData.map((r) => r.id);
                const currentIdx = ids.indexOf(row.id);
                if (currentIdx !== -1 && currentIdx < ids.length - 1) {
                  const nextId = ids[currentIdx + 1];
                  const nextInput = inputRefs.current[nextId];
                  if (nextInput) nextInput.focus();
                }
              }, 0);
            }}
            style={{ height: 34, padding: "0 10px", minWidth: isMobile ? 120 : 150, maxWidth: isMobile ? 170 : undefined }}
          />
        );
      },
    },
    { key: "shipmentDate", label: "Shipment Date", width: 190 },
    {
      key: "action",
      label: "Action",
      width: 130,
      align: "center",
      render: (row) => {
        const isSelected = selectedRows.some((selectedRow) => selectedRow.id === row.id);
        const isDisabled = row.disabled;
        return (
          <TooltipProvider>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <ActionButton title="Good" color="#4CAF50" disabled={isSelected || isDisabled} onClick={() => handleApproveShipment(row)}>
                <Check size={15} />
              </ActionButton>
              <ActionButton title="Damaged" color="#FF3D00" disabled={isSelected || isDisabled} onClick={() => handleRejectShipment(row)}>
                <X size={15} />
              </ActionButton>
              {row?.manualEntry && (
                <ActionButton title="Delete Row" color="#c3c8cc" disabled={isSelected || isDisabled} onClick={() => handleDeleteManualShipment(row)}>
                  <Trash2 size={15} />
                </ActionButton>
              )}
            </div>
          </TooltipProvider>
        );
      },
    },
  ];

  const inboundDetailscolumns = [
    { key: "upc", label: "UPC" },
    { key: "qty", label: "QTY", align: "center" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span style={{ color: row?.status === "Damaged" ? "#FF3D00" : row?.status === "Ok" ? "#4CAF50" : "#c3c8cc", fontWeight: 700 }}>
          {row?.status}
        </span>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <PageHeader title="Track Inbound Item" subtitle="Track the status of your incoming shipments" />

        <section style={{ ...panelStyle, padding: isMobile ? 14 : 18 }}>
          <Field label="Enter Tracking ID">
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, alignItems: isMobile ? "stretch" : "flex-start", width: "100%" }}>
              <div style={{ flex: 1, width: "100%", minWidth: 0 }}>
                <Input
                  placeholder="Enter tracking ID"
                  value={trackingId}
                  onChange={handleTrackingIdChange}
                  aria-invalid={!!trackingIdError}
                  style={{ width: "100%", height: 44 }}
                />
                {trackingIdError && <div style={helperTextStyle}>{trackingIdError}</div>}
              </div>
              <Button
                type="button"
                onClick={handleSearchByTrackingID}
                disabled={Boolean(!trackingId.trim() || trackingIdError)}
                style={{ width: isMobile ? "100%" : 110, height: 44 }}
              >
                Search
              </Button>
            </div>
          </Field>
        </section>

        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              gap: 12,
            }}
          >
            <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Search Results for {searchResult}</div>
            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                <div style={{ width: "100%" }}>
                  <SearchBar searchTerm={searchTerm} setSearchTerm={handleSearch} placeholder="Search" />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddNewItem}
                    disabled={disableAddItem}
                    style={{ flex: 1, height: 36 }}
                  >
                    Add Item
                  </Button>
                  {filteredData.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleOpenScanDialog}
                      style={{ flex: 1, height: 36, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                    >
                      <ScanLine size={14} /> Scan
                    </Button>
                  )}
                  {multiRowsSelected && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleAllApprovalDialogOpen}
                      style={{ width: 36, height: 36, borderColor: "#4CAF50", color: "#4CAF50", background: "rgba(76,175,80,0.1)" }}
                      title="Approve All"
                    >
                      <Check size={16} />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 8 }}>
                {filteredData.length > 0 && (
                  <Button type="button" variant="outline" onClick={handleOpenScanDialog}>
                    <ScanLine size={14} /> Scan
                  </Button>
                )}
                {multiRowsSelected && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleAllApprovalDialogOpen}
                          style={{ borderColor: "#4CAF50", color: "#4CAF50", background: "rgba(76,175,80,0.1)" }}
                        >
                          <Check size={16} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Approve All</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <Button type="button" variant="outline" onClick={handleAddNewItem} disabled={disableAddItem}>
                  Add Item
                </Button>
                <SearchBar searchTerm={searchTerm} setSearchTerm={handleSearch} placeholder="Search" />
              </div>
            )}
          </div>

          <DataTable
            columns={columns}
            rows={paginatedRows}
            totalRows={filteredData.length}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            loading={showLoader}
            minWidth={980}
            onSelectionChange={handleSelectionChange}
            selectionRows={filteredData}
            isSelectable
            resetSelection={resetSelection}
            getRowCheckboxDisabled={(row) => !!row.disabled}
          />
        </section>

        {!disableButtons && (
          <div style={{ display: "flex", justifyContent: isMobile ? "stretch" : "flex-end", gap: 10, width: isMobile ? "100%" : "auto" }}>
            <Button type="button" variant="outline" onClick={() => navigate("/warehouse/inbound")} style={{ flex: isMobile ? 1 : "none", width: isMobile ? "auto" : 120 }}>
              Cancel
            </Button>
            <Button type="button" onClick={handleItemDetails} style={{ flex: isMobile ? 1 : "none", width: isMobile ? "auto" : 120 }}>
              Submit
            </Button>
          </div>
        )}
      </div>

      <Dialog open={openAddItemDialog} onOpenChange={(open) => !open && handleNewItemDialogClose()}>
        <DialogContent style={{ maxWidth: 420 }}>
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="UPC*">
              <Input
                type="number"
                value={newItem.upc}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length > 14) return;
                  setNewItem({ ...newItem, upc: val });
                }}
                onBlur={(e) => handleGetUpcData(e)}
                onKeyDown={blockInvalidInt}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^\d+$/.test(pasteData)) e.preventDefault();
                }}
                onWheel={(event) => event.target.blur()}
                maxLength={14}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                style={{ appearance: "textfield", MozAppearance: "textfield" }}
              />
              {!!newItem.upc && (newItem.upc.length < 11 || newItem.upc.length > 14) && <div style={helperTextStyle}>UPC must be 11 to 14 digits</div>}
            </Field>
            <Field label="Item Name">
              <Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
              {trackingItemNameError && <div style={helperTextStyle}>{trackingItemNameError}</div>}
            </Field>
            <Field label="Shipment ID">
              <Input value={newItem.shipmentId} onChange={(e) => setNewItem({ ...newItem, shipmentId: e.target.value })} />
            </Field>
            <Field label="Quantity">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button type="button" onClick={handleDecrease} style={{ background: "none", border: "none", color: "#c3c8cc", cursor: "pointer", padding: 4, display: "flex" }}>
                  <MinusCircle size={20} />
                </button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue === "") {
                      setQuantity("");
                      return;
                    }
                    const newValue = Number(e.target.value);
                    handleQuantityChange(newValue);
                  }}
                  onKeyDown={blockInvalidInt}
                  onPaste={(e) => {
                    const pasteData = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasteData)) e.preventDefault();
                  }}
                  onWheel={(event) => event.target.blur()}
                  style={{ textAlign: "center", appearance: "textfield", MozAppearance: "textfield" }}
                />
                <button type="button" onClick={handleIncrease} style={{ background: "none", border: "none", color: "#c3c8cc", cursor: "pointer", padding: 4, display: "flex" }}>
                  <PlusCircle size={20} />
                </button>
              </div>
            </Field>
          </div>
          <DialogFooter style={{ justifyContent: "center", gap: 10 }}>
            <Button type="button" variant="outline" onClick={handleNewItemDialogClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAddItemSubmit} disabled={!newItem.upc || newItem.upc.length < 11 || newItem.upc.length > 14}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openScanDialog} onOpenChange={(open) => !open && handleCloseScanDialog()}>
        <DialogContent style={{ maxWidth: 420 }}>
          <DialogHeader>
            <DialogTitle>Scan UPC</DialogTitle>
            <DialogDescription>Scan or paste UPC below</DialogDescription>
          </DialogHeader>
          <Input value={scanInput} onChange={handleScanInputChange} onPaste={handleScanPaste} onKeyDown={handleScanKeyDown} placeholder="Scan or paste here" autoFocus />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
            {scanSerials.map((serial, idx) => (
              <span
                key={serial + idx}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 999,
                  padding: "4px 8px",
                  color: "#fff",
                  fontSize: 12,
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                {serial}
                <button type="button" onClick={() => handleDeleteChip(serial)} aria-label={`Remove ${serial}`} style={{ border: "none", background: "transparent", color: "#c3c8cc", cursor: "pointer", display: "flex", padding: 0 }}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          {scanError && <div style={helperTextStyle}>{scanError}</div>}
          <DialogFooter style={{ justifyContent: "center", gap: 10 }}>
            <Button type="button" variant="outline" onClick={handleCloseScanDialog}>
              Cancel
            </Button>
            <Button type="button" onClick={handleScanSubmit}>
              Scan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openStatusDialog} onOpenChange={(open) => open && setOpenStatusDialog(true)}>
        <DialogContent style={{ maxWidth: 560 }} showCloseButton={false}>
          <DialogHeader>
            <DialogTitle style={{ textAlign: "center" }}>Update Shipment Status</DialogTitle>
          </DialogHeader>
          <div style={{ ...panelStyle, padding: 16, display: "flex", gap: 12, borderLeft: "4px solid #ff9800" }}>
            <CircleAlert size={22} color="#ff9800" style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Status Change</div>
              <div style={{ color: "#c3c8cc", fontSize: 13, lineHeight: 1.5 }}>
                The shipment associated with the entered tracking number has not been shipped yet. Please click the 'Mark as Shipped' button below to manually mark the shipment as shipped and create the payout.
              </div>
            </div>
          </div>
          <DialogFooter style={{ justifyContent: "center", gap: 10 }}>
            <Button type="button" variant="outline" onClick={() => navigate("/warehouse/inbound")}>
              Cancel
            </Button>
            <Button type="button" onClick={handleUpdateStatus}>
              Mark as Shipped
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openAddDetailsDialog} onOpenChange={(open) => !open && handleAddDetailsDialogClose()}>
        <DialogContent style={{ maxWidth: 430 }}>
          <DialogHeader>
            <DialogTitle>Add Details</DialogTitle>
          </DialogHeader>
          <div style={{ ...panelStyle, padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Serial No">
              <Input placeholder="Enter Serial No" value={serialNo} onChange={(e) => setSerialNo(e.target.value)} />
            </Field>
            <Field label="Note">
              <Textarea placeholder="Add Note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Field label="Upload Images">
              <button
                type="button"
                onClick={() => document.getElementById("image-upload-input").click()}
                style={{
                  border: "1px dashed #00ADB5",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                  color: "#00ADB5",
                  cursor: "pointer",
                  background: "rgba(0,173,181,0.04)",
                  width: "100%",
                }}
              >
                <input
                  id="image-upload-input"
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) setUploadedImages(Array.from(e.target.files));
                  }}
                />
                {uploadedImages.length > 0 ? (
                  <span style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                    {uploadedImages.map((img, idx) => (
                      <img key={idx} src={URL.createObjectURL(img)} alt={`Preview ${idx + 1}`} style={{ maxWidth: 80, maxHeight: 80, marginBottom: 8, borderRadius: 4 }} />
                    ))}
                  </span>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Upload size={15} /> Upload Image
                  </span>
                )}
              </button>
            </Field>
          </div>
          <DialogFooter style={{ justifyContent: "center", gap: 10 }}>
            <Button type="button" variant="outline" onClick={handleAddDetailsDialogClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleItemRejectDetails}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openDetailsUpdatedDialog} onOpenChange={(open) => !open && handleDetailsUpdatedDialogClose()}>
        <DialogContent style={{ maxWidth: 720 }}>
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <CheckCircle2 style={{ color: "#00838F", backgroundColor: "#00838f3b", borderRadius: "50%", padding: 5, width: 56, height: 56 }} />
            <DialogTitle style={{ marginTop: 8 }}>Inbound Details Updated Successfully</DialogTitle>
            <DialogDescription>The status of all inbound items has been recorded and inventory updated accordingly.</DialogDescription>
          </div>
          <div style={{ ...panelStyle, padding: 16 }}>
            <div style={{ color: "#fff", fontWeight: 800, marginBottom: 12 }}>Items Added</div>
            <DataTable columns={inboundDetailscolumns} rows={inboundItemStatusDetails} totalRows={inboundItemStatusDetails.length} currentPage={0} rowsPerPage={inboundItemStatusDetails.length || 5} loading={false} minWidth={420} />
          </div>
          <DialogFooter style={{ justifyContent: "center", gap: 10 }}>
            <Button type="button" variant="outline" onClick={handleDetailsUpdatedDialogClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleInboundDetailsUpdate}>
              Go to Inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openAllApprovalDialog} onOpenChange={(open) => !open && handleAllApprovalDialogClose()}>
        <DialogContent style={{ maxWidth: 380 }}>
          <DialogHeader>
            <DialogTitle>Approve Items</DialogTitle>
          </DialogHeader>
          <div style={{ ...panelStyle, padding: 16, textAlign: "center", color: "#fff", fontWeight: 800 }}>{selectedRows?.length} items are being approved</div>
          <DialogFooter style={{ justifyContent: "center" }}>
            <Button type="button" onClick={handleMultiItemsApproval}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseTrackInboundItem;
